import { toE164 } from '@/lib/phone';

import { apiClient } from './apiClient';

import type {
  ApplicationListResponse,
  DraftResponse,
  GetApplicationResponse,
  PrefillData,
  PrefillResponse,
  SaveDraftRequest,
  SubmitResponse,
} from '@/types/applicationApi.types';

function prepareRequestData(data: SaveDraftRequest, excludeCurrentStep = false): SaveDraftRequest {
  const prepared = {
    ...data,
    phone: data.phone ? toE164(data.phone) : undefined,
  };

  if (excludeCurrentStep) {
    delete prepared.currentStep;
  }

  return prepared;
}

export const applicationApi = {
  /**
   * Get the current user's draft application
   * Returns 404 if no draft exists
   */
  getDraft: (jobPostingId?: string) =>
    apiClient.get<DraftResponse>(`/applications/draft${jobPostingId ? `?jobPostingId=${jobPostingId}` : ''}`),

  /**
   * Create a new draft or save to existing draft
   * Call when user clicks "Next" to validate and save current step
   */
  saveDraft: (data: SaveDraftRequest) => apiClient.post<DraftResponse>('/applications/draft', prepareRequestData(data)),

  /**
   * Update an existing draft
   * Same as saveDraft but uses PUT method
   */
  updateDraft: (data: SaveDraftRequest) =>
    apiClient.put<DraftResponse>('/applications/draft', prepareRequestData(data)),

  /**
   * Submit the final application
   * Performs full validation across all steps
   */
  submitApplication: (data: SaveDraftRequest) =>
    apiClient.post<SubmitResponse>('/applications/submit', prepareRequestData(data, true)),

  /**
   * List all applications for the current applicant
   */
  listApplications: () => apiClient.get<ApplicationListResponse>('/applications'),

  /**
   * Get a single application by ID
   */
  getApplication: (applicationId: string) => apiClient.get<GetApplicationResponse>(`/applications/${applicationId}`),

  /**
   * Get prefill data from the applicant's most recent submitted application.
   * Returns null if there is no prior application (204 No Content).
   */
  getPrefill: async (): Promise<PrefillData | null> => {
    // Use fetch directly to handle 204 No Content (no body to parse)
    const stored = localStorage.getItem('kent-auth-session');
    const token = stored
      ? ((JSON.parse(stored) as { state?: { accessToken?: string } }).state?.accessToken ?? null)
      : null;
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch('/api/applications/prefill', { method: 'GET', headers });
    if (response.status === 204) {
      return null;
    }
    const body = (await response.json()) as PrefillResponse;
    return body.data.prefill;
  },
};
